import React, { useEffect, useState } from 'react';
import {
  Container, Box, Typography, Grid, Card, CardContent, Stack, LinearProgress,
  Chip, Paper, Divider, Alert, CircularProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStudyPlans } from '../context/StudyPlansContext';
import { httpGet } from '../lib/http';

export default function StudyPlanAnalytics() {
  const { pathId } = useParams();
  const { user } = useAuth();
  const { getTrack } = useStudyPlans();
  const navigate = useNavigate();

  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        // Get plan
        const cached = getTrack(pathId);
        if (cached) {
          setPlan(cached);
        }

        // Get progress data
        const userId = user?.id || user?.userId || user?._id;
        const progressRes = await httpGet(
          `/api/users/${encodeURIComponent(userId)}/path-progress/${encodeURIComponent(pathId)}`
        );
        
        if (progressRes && progressRes.ok) {
          const progressData = await progressRes.json();
          setProgress(progressData);

          // Calculate analytics
          const topicProgress = progressData.topicProgress || [];
          const completedTopics = topicProgress.filter(tp => tp.lessonCompleted).length;
          const totalTopics = topicProgress.length;
          const completionRate = totalTopics > 0 ? (completedTopics / totalTopics) * 100 : 0;

          // Subject-wise breakdown
          const subjectMap = {};
          const subjects = cached?.subjects || cached?.orderedSubjects || [];
          
          subjects.forEach(subject => {
            const subjectTopics = (subject.topics || []);
            const subjectTopicIds = subjectTopics.map(t => t.id || t.title);
            const completed = topicProgress.filter(
              tp => subjectTopicIds.includes(tp.topicId) && tp.lessonCompleted
            ).length;
            
            subjectMap[subject.id || subject.title] = {
              title: subject.title,
              total: subjectTopicIds.length,
              completed,
              percentage: subjectTopicIds.length > 0 ? (completed / subjectTopicIds.length) * 100 : 0
            };
          });

          // Calculate streak (simplified)
          const recentActivity = topicProgress
            .filter(tp => tp.completedAt)
            .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
          
          const streak = calculateStreak(recentActivity);

          setAnalytics({
            completedTopics,
            totalTopics,
            completionRate,
            subjectBreakdown: subjectMap,
            streak,
            recentActivity: recentActivity.slice(0, 5),
            estimatedTimeRemaining: calculateTimeRemaining(cached, completedTopics, totalTopics)
          });
        }
      } catch (err) {
        console.error('Error loading analytics:', err);
      } finally {
        setLoading(false);
      }
    }

    if (user && pathId) {
      loadData();
    }
  }, [pathId, user, getTrack]);

  function calculateStreak(activities) {
    if (!activities || activities.length === 0) return 0;
    
    // Simplified streak calculation
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let streak = 0;
    let currentDate = new Date(today);
    
    for (let activity of activities) {
      const activityDate = new Date(activity.completedAt);
      activityDate.setHours(0, 0, 0, 0);
      
      if (activityDate.getTime() === currentDate.getTime()) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else if (activityDate.getTime() < currentDate.getTime()) {
        break;
      }
    }
    
    return streak;
  }

  function calculateTimeRemaining(plan, completed, total) {
    if (!plan || total === 0) return 0;
    const totalHours = plan.totalEstimatedHours || 0;
    const remainingPercentage = ((total - completed) / total) * 100;
    return Math.round((totalHours * remainingPercentage) / 100);
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!plan || !analytics) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="info">No analytics data available for this study plan.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700}>
          Progress Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {plan.title}
        </Typography>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 1.5, borderRadius: '16px' }}>
                  <CheckCircleIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {analytics.completedTopics}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Topics Completed
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ bgcolor: 'success.main', color: 'white', p: 1.5, borderRadius: '16px' }}>
                  <TrendingUpIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {Math.round(analytics.completionRate)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completion Rate
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ bgcolor: 'warning.main', color: 'white', p: 1.5, borderRadius: '16px' }}>
                  <ScheduleIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {analytics.estimatedTimeRemaining}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hours Remaining
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ bgcolor: 'error.main', color: 'white', p: 1.5, borderRadius: '16px' }}>
                  <TrophyIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {analytics.streak}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Day Streak
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Subject-wise Progress */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
            Subject-wise Progress
          </Typography>
          <Stack spacing={3}>
            {Object.values(analytics.subjectBreakdown).map((subject, idx) => (
              <Box key={idx}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography fontWeight={600}>{subject.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {subject.completed} / {subject.total} topics ({Math.round(subject.percentage)}%)
                  </Typography>
                </Stack>
                  <LinearProgress
                  variant="determinate"
                  value={subject.percentage}
                  sx={{ height: 8, borderRadius: '16px' }}
                />
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Recent Activity
          </Typography>
          {analytics.recentActivity.length === 0 ? (
            <Typography color="text.secondary">No recent activity</Typography>
          ) : (
            <Stack spacing={2}>
              {analytics.recentActivity.map((activity, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography fontWeight={600}>{activity.topicId}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Completed
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(activity.completedAt).toLocaleDateString()}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
